"use client";

import { useLayoutEffect, type RefObject } from "react";

export type PopoverAlign = "left" | "right" | "center";
export type PopoverPlacement = "top" | "bottom";

/**
 * Shared placement for every popover (menus, reaction / sticker pickers).
 * The panel must be `position: fixed`: it is placed against the anchor's
 * viewport rect, so no scroll/overflow ancestor (a chat's message list, a
 * docked side panel) can clip it, then clamped inside the viewport and
 * flipped to the other side when it doesn't fit. Re-placed on scroll/resize
 * and when the panel's own size changes (e.g. content finishes loading).
 */
export function useAnchoredPopover(
  open: boolean,
  anchorRef: RefObject<HTMLElement | null>,
  panelRef: RefObject<HTMLElement | null>,
  {
    align = "left",
    placement = "bottom",
    gap = 6,
    margin = 12,
  }: {
    align?: PopoverAlign;
    placement?: PopoverPlacement;
    gap?: number;
    margin?: number;
  } = {}
): void {
  useLayoutEffect(() => {
    const panel = panelRef.current;
    const anchor = anchorRef.current;
    if (!open || !panel || !anchor) return;
    const place = () => {
      const a = anchor.getBoundingClientRect();
      const vw = document.documentElement.clientWidth;
      const vh = document.documentElement.clientHeight;
      const w = panel.offsetWidth;
      const h = panel.offsetHeight;

      let left =
        align === "right"
          ? a.right - w
          : align === "center"
            ? a.left + a.width / 2 - w / 2
            : a.left;
      left = Math.max(margin, Math.min(left, vw - margin - w));

      const above = a.top - gap - h;
      const below = a.bottom + gap;
      let top = placement === "top" ? above : below;
      // Flip when the preferred side lacks room and the other side has it.
      if (placement === "top" && above < margin && below + h <= vh - margin) top = below;
      if (placement === "bottom" && below + h > vh - margin && above >= margin) top = above;
      top = Math.max(margin, Math.min(top, vh - margin - h));

      panel.style.left = `${Math.round(left)}px`;
      panel.style.top = `${Math.round(top)}px`;
    };
    place();
    const resize = new ResizeObserver(place);
    resize.observe(panel);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      resize.disconnect();
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, anchorRef, panelRef, align, placement, gap, margin]);
}
